/**
 * @file 发送上报
 * @author dafo<huanghouin@baijiahulian.com>
 * @module util/request
 */
import {toQuery, parse} from './json';
import delay from './delay';
import storage from './storage';
import {
    Config,
    extendCommonField,
    isReady,
    onReady,
    publicParams
} from './config';
import xhr from './xhr';


/**
 * 由于不同类型的日志需要上报给不同的域名(gsxtj或者qingzhou)，在此动态获取获取上报的域名
 *
 * @param {Object} jsonOrString 上报参数对象, 沿用原有的变量命名
 * @returns {Array.<string>} 上报域名数组
 */
function getRequestDomains(jsonOrString = {}) {
    if (!jsonOrString) {
        return [Config.DOMAIN];
    }
    // TODO 暂时 fmp/rmp perf.gif 接口只上报到大数据，仅作分析对比，后续要去掉
    if (jsonOrString.only_big_data) {
        return [Config.DOMAIN];
    }

    if (['page_load'].includes(jsonOrString.req_type)) {
        return [Config.DOMAIN, Config.QINGZHOU_DOMAIN];
    }
    // 这块的逻辑
    const reqTypes = ['api_call', 'log', 'fmp', 'rmp'];
    if (reqTypes.includes(jsonOrString.req_type) || jsonOrString.metric) {
        return [Config.QINGZHOU_DOMAIN];
    }

    return [Config.DOMAIN];

}

/**
 * 发送上报请求
 *
 * @function request.post
 * @param {string} url 上报地址
 * @param {Object} jsonOrString 键值对对象或字符串
 * @param {Function=} callback 上报后的回调
 * @param {Array=} data post 的数据
 */
export const post = (url, jsonOrString = {}, callback, data = []) => {
    let domains = [];
    if (url.indexOf('?') > -1) {
        // 命中了日志重发机制，注意处理不能重复上报相同的参数，造成异常日志
        // 比如url=time.gif?url=http%3A%2F%2Ftest-m.gaotu.cn%2F&uuid=xxx
        const query = url.split('?')[1];
        const queryObj = parse(query);
        domains = getRequestDomains(queryObj);
    }
    else {

        domains = getRequestDomains(jsonOrString);

        // 带有abtest等公共参数，目前只发送给哈勃大数据服务，不发送到轻舟（除了perf.gif）
        if (domains.includes(Config.DOMAIN)) {
            Object.assign(jsonOrString, publicParams);
        }
        // 这里的 type 是上报类型对应的地址，比如 view 事件，对应的默认就是 pv0.gif 。
        const query = toQuery(extendCommonField(jsonOrString, {
            type: url
        }));

        if (query) {
            url += `?${query}`;
        }

        storage.save(url, data);
        storage.pending(url);
    }

    // 静态文件是否通过file协议
    const isFileProtocol = document && document.location.protocol === 'file:';

    domains.forEach(domain => {
        // 通过file协议访问时添加https协议
        const targetURL = isFileProtocol ? `https:${domain}${url}` : domain + url;
        const isSuccess = data.length
            ? navigator.sendBeacon(targetURL, JSON.stringify(data))
            : navigator.sendBeacon(targetURL);

        if (isSuccess && navigator.onLine) {
            storage.remove(url, data);
            storage.resend(post);
        }
        else {
            storage.deletePending(url);
        }

        callback && callback();
    });

};

const delayPost = delay(post);

/**
 * 发送上报请求
 *
 * @function request.get
 * @param {string} url 上报地址
 * @param {Object} jsonOrString 键值对对象或字符串
 * @param {Function=} callback 上报后的回调
 */
export const get = (url, jsonOrString = {}, callback) => {
    let domains = [];
    if (url.indexOf('?') > -1) {
        // 命中了日志重发机制，注意处理不能重复上报相同的参数，造成异常日志
        // 比如url=time.gif?url=http%3A%2F%2Ftest-m.gaotu.cn%2F&uuid=xxx
        const query = url.split('?')[1];
        const queryObj = parse(query);
        domains = getRequestDomains(queryObj);
    }
    else {
        if (!jsonOrString) {
            jsonOrString = {};
        }
        domains = getRequestDomains(jsonOrString);

        // 带有abtest等公共参数，目前只发送给哈勃大数据服务，不发送到轻舟（除了perf.gif）
        if (domains.includes(Config.DOMAIN)) {
            Object.assign(jsonOrString, publicParams);
        }

        const query = toQuery(extendCommonField(jsonOrString, {
            type: url
        }));

        if (query) {
            url += `?${query}`;
        }

        storage.save(url);
    }

    // 静态文件是否通过file协议
    const isFileProtocol = document && document.location.protocol === 'file:';

    domains.forEach(domain => {
        // 通过file协议访问时添加https协议
        const targetURL = isFileProtocol ? `https:${domain}${url}` : domain + url;

        xhr(targetURL, isSuccess => {

            if (isSuccess) {
                storage.remove(url);
                storage.resend(get);
            }
            else {
                storage.deletePending(url);
            }

            callback && callback();
        });

        storage.pending(url);
    });
};

const delayGet = delay(get);

/**
 * 发送上报请求
 *
 * @param {string=} verb HTTP Verb
 * @param {boolean} isRunAtOnce 是否立即上报
 * @return {Function} 选择到的上报提交方法
 */
export default function request(verb, isRunAtOnce) {
    const isImmediateSend = isRunAtOnce || !Config.DELAY;
    const requester = String(verb).toLowerCase() === 'post' && navigator.sendBeacon
        ? (isImmediateSend ? post : delayPost)
        : (isImmediateSend ? get : delayGet);

    if (isReady()) {
        return requester;
    }

    return (...args) => onReady(() => requester(...args));
}

/**
 * 简单实现发起请求的能力的封装，仅供内部使用,直接用第三方库比如axios 会使包体积过大，同时代码编译也有问题
 *
 * @param {Object} options 发送请求的参数
 * @return  {Promise<Object>}
 */
export const ajax = options => new Promise((resolve, reject) => {
    const {url = '', method = 'POST', headers = {}, data = {}} = options;
    if (url) {
        const xhr = new XMLHttpRequest();
        xhr.open(method.toUpperCase(), url);

        const contentType = headers['content-type'] || headers['Content-Type'];
        let sendData = '';
        if (!contentType) {
            headers['content-type'] = 'application/json';
        }
        Object.keys(headers).forEach(key => {
            xhr.setRequestHeader(key.toLowerCase(), headers[key]);
        });
        if (headers['content-type'] === 'application/json') {
            sendData = JSON.stringify(data);
        }
        if (headers['content-type'] === 'application/x-www-form-urlencoded') {
            sendData = Object.keys(data).map(key => {
                return `${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`;
            });
        }

        xhr.addEventListener('load', e => {
            if (xhr.readyState === XMLHttpRequest.DONE || (xhr.readyState === XMLHttpRequest.prototype.DONE)) {
                let resData = {};
                if (xhr.status === 200 && xhr.responseText) {
                    try {
                        resData = JSON.parse(xhr.responseText);
                        resolve(resData);
                    }
                    catch (e) {
                        // 后期补充其他类型的返回值
                    }
                }
                else {
                    reject(xhr.responseText);
                }
            }
        });

        // 其他情况暂时不处理
        xhr.send(sendData);

    }
});
